import React, { useContext } from 'react';

import Badge from './Badge';
import Box from '../../../components/Box/Box';
import Icon from '../../../components/Icon/Icon';
import { Link } from '../../../components/Link';
import { PrimaryButton, ButtonIcon } from '../../../components/Button';
import Cart from './Cart';

import HeaderNavBarContext from '../context/HeaderNavBarContext';

import UserDropdownMenu from './UserDropdownMenu';
import BadgeCounter from './BadgeCounter';

import fireEvent from '../utils/fireEvent';
import useTarget from '../hooks/useTarget';
import type { TMenuLink } from 'types/TMenuLink';
import type { TLocaleAlt, TMessages } from 'types/THeaderConfig';
import BrazeContentCardCount from './BrazeContentCardCount';

type TUserLinksProps = {
  showHamburgerMenu: () => void;
  menuLinks?: TMenuLink[];
};

const ChangeLanguage: React.FC<{ localeAlt: TLocaleAlt | undefined }> = ({ localeAlt }) => {
  if (!localeAlt) return null;
  return (
    <li className="hz-hide-lte-md">
      <Link href={localeAlt.url} data-role={localeAlt.role} tabIndex={0}>
        {localeAlt?.text?.toUpperCase()}
      </Link>
    </li>
  );
};

const UserLinks = ({ showHamburgerMenu, menuLinks }: TUserLinksProps) => {
  const loginTarget = useTarget('');

  const navBar = useContext(HeaderNavBarContext);
  const { userDetails, userLinks, ssoLinks, showBrazeContentCardCount } = navBar ?? {};

  const isLoggedIn = userDetails?.isLoggedIn;

  const handlePlaceAd = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const placeAdEvent = fireEvent(
      'placead',
      {},
      {
        cancelable: true,
        node: event.target as HTMLElement,
      },
    );

    if (placeAdEvent.defaultPrevented) {
      event.preventDefault();
    }
  };

  return (
    <Box as="ul" parent="Header" name="menu" data-role="nav-dropdown-menu">
      {userLinks?.messages && (
        <li className="hz-hide-custom--messages">
          <Link
            href={userLinks.messages.url}
            data-sem="messagingDisplayAction"
            data-role={userLinks.messages.role}
            title={userLinks.messages.text}
            tabIndex={0}
          >
            <Box as="div" parent="Button" name="badge">
              <Icon name="messages" color={'text-controls-default'} />
              {isLoggedIn && (
                <BadgeCounter
                  endpoint={userLinks.messages.badge.endpoint}
                  badgeKey={userLinks.messages.badge.key}
                  badgeName={userLinks.messages.badge.name}
                />
              )}
              <span className="hz-hide-lte-md">{userLinks.messages.text}</span>
            </Box>
          </Link>
        </li>
      )}

      {userLinks?.notifications && (
        <li>
          <Link
            role="button"
            href={userLinks.notifications.url}
            data-role={userLinks.notifications.role}
            title={userLinks.notifications.text}
            tabIndex={0}
          >
            <Box as="div" parent="Button" name="badge">
              <Icon name="notification" color={'text-controls-default'} />
              {isLoggedIn && (
                <BrazeContentCardCount isEnabled={showBrazeContentCardCount}>
                  {(extraCount) => (
                    <BadgeCounter
                      endpoint={(userLinks.notifications as TMessages).badge.endpoint}
                      badgeKey={(userLinks.notifications as TMessages).badge.key}
                      badgeName={(userLinks.notifications as TMessages).badge.name}
                      extraCount={extraCount}
                    />
                  )}
                </BrazeContentCardCount>
              )}
              {!isLoggedIn && <Badge kind="signalAction" />}
              <span className="hz-hide-lte-md">{userLinks.notifications.text}</span>
            </Box>
          </Link>
        </li>
      )}

      {isLoggedIn && userLinks?.shoppingCart && <Cart cart={userLinks.shoppingCart} />}

      {!isLoggedIn && (
        <>
          <li className="hz-hide-gte-lg">
            <Link data-role="hamburgerOpen" onClick={showHamburgerMenu} tabIndex={0}>
              <Icon name="profile" color={'text-controls-default'} />
            </Link>
          </li>

          <li className="hz-hide-lte-md">
            <Link href={`${ssoLinks?.login.url}?target=${loginTarget}`} data-role={ssoLinks?.login.role} tabIndex={0}>
              <Icon name="profile" color={'text-controls-default'} />
              <span>{ssoLinks?.login.text}</span>
            </Link>
          </li>
        </>
      )}

      {isLoggedIn && <UserDropdownMenu showHamburgerMenu={showHamburgerMenu} menuLinks={menuLinks} />}

      {userLinks?.placeAd && (
        <li>
          <PrimaryButton
            as="a"
            href={userLinks.placeAd.url}
            onClick={handlePlaceAd}
            className="hz-Button--callToAction"
            data-role={userLinks.placeAd.role}
            tabIndex={0}
            aria-label={userLinks.placeAd.text}
          >
            <ButtonIcon name="pin-callToAction-foreground" />
            <span className="hz-hide-lte-md">{userLinks.placeAd.text}</span>
          </PrimaryButton>
        </li>
      )}
      <ChangeLanguage localeAlt={userLinks?.localeAlt} />
    </Box>
  );
};

export default UserLinks;
